import { giphyDarkestGrey, giphyLightCharcoal } from '@giphy/colors'
import download from 'downloadjs'
import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router'
import { setCreationEditedFile } from 'shared/redux/creation-tool'
import convertFramesToVideo from 'shared/redux/creation-tool-to-video'
import { getCreationFile } from 'shared/redux/reducers'
import { trackBackdropStickers, trackDownloadBackdrop, trackUploadBackdrop } from 'shared/util/gif-maker-ga-events'
import styled from 'styled-components'
import AnonymousUploadWarning from '../../anonymous-upload-warning'
import { UPLOAD, ZOOM_MAKER } from '../../route'
import { ZoomMakerFile, ZoomMakerFrame, ZoomMakerStatus } from '../types'
import Video from '../welcome/components/background-video'
import Buttons from './components/buttons'
import Instructions from './components/instructions'
import Preview from './components/preview'
import { ZoomMakerDownloadContext } from './context'

const AFTER_COMPLETE_DELAY = 1500

const Container = styled.div`
    padding: 50px 0;
`

const Content = styled.div`
    background: ${giphyDarkestGrey};
    border-radius: 4px;
    box-sizing: border-box;
    margin: 0 auto;
    padding: 50px;
    position: relative;
    width: 900px;
`

const FeaturedText = styled.p`
    color: ${giphyLightCharcoal};
    font-size: 15px;
    font-weight: bold;
    margin: 0 auto;
    text-align: center;
    width: 300px;
`

type Props = {
    file: ZoomMakerFile
    canvasFrames: ZoomMakerFrame[]
    canvasStickers: any[]
    setCreationEditedFile: (file: any) => void
}

const ZoomMakerDownload = ({ file, canvasFrames, canvasStickers, setCreationEditedFile }: Props) => {
    const { push } = useHistory()
    const [status, setStatus] = useState<ZoomMakerStatus>(ZoomMakerStatus.Idle)
    const [hasDownloaded, setHasDownloaded] = useState<boolean>(false)
    const videoBlobRef = useRef(null)
    const stickerIds = canvasStickers.map(({ id }) => id)

    useEffect(() => {
        let delay: null | ReturnType<typeof setTimeout> = null
        if (status === ZoomMakerStatus.Complete) {
            // @ts-ignore
            delay = setTimeout(
                () => {
                    setStatus(ZoomMakerStatus.Idle)
                    setHasDownloaded(true)
                },
                hasDownloaded ? 0 : AFTER_COMPLETE_DELAY
            )
        }
        return () => {
            delay && clearTimeout(delay)
        }
    }, [status])

    const generateVideo = async () => {
        if (videoBlobRef.current) return
        const blob = await convertFramesToVideo({ frames: canvasFrames, jobId: 'giphybackdrop' })
        videoBlobRef.current = blob

        // store the video for use in upload
        setCreationEditedFile({
            file: new File([blob], 'giphy-backdrop.mp4', { type: 'video/mp4', lastModified: Date.now() }),
            type: 'backdrop',
        })
    }

    const onDownload = async () => {
        setStatus(ZoomMakerStatus.Downloading)
        await generateVideo()
        trackDownloadBackdrop(file.isStarter ? file.id : '')
        trackBackdropStickers(stickerIds.join(','))
        download(videoBlobRef.current, 'giphy-backdrop.mp4', 'video/mp4')
        setStatus(ZoomMakerStatus.Complete)
    }

    const onUpload = () => {
        trackUploadBackdrop(file.isStarter ? file.id : '')
        push(`${ZOOM_MAKER}${UPLOAD}`)
    }

    return (
        <ZoomMakerDownloadContext.Provider
            value={{
                frames: canvasFrames,
                hasDownloaded,
                status,
                setStatus,
                onDownload,
                onUpload,
            }}
        >
            <Container>
                <AnonymousUploadWarning />
                <Video />
                <Content>
                    {hasDownloaded ? <Instructions /> : <Preview />}
                    <Buttons />
                    <FeaturedText>Upload your Backdrop to GIPHY to share it and potentially be featured!</FeaturedText>
                </Content>
            </Container>
        </ZoomMakerDownloadContext.Provider>
    )
}

export default connect((state) => ({ file: getCreationFile(state) }), { setCreationEditedFile })(ZoomMakerDownload)
